import React, { PureComponent, Fragment } from 'react';
import classnames from 'classnames';
import Swiper from 'swiper/dist/js/swiper';
import { connect } from 'unistore/react';
import queryString from 'query-string';

import 'swiper/dist/css/swiper.css';

import actions from '~/scripts/state/actions';
import OverallResult from '~/scripts/OverallResult';
import Icon from '~/core/scripts/components/Icon';
import { is768Query } from '~/core/scripts/mediaquery-helper';
import { isNumeric, sortByKey, numberFormat } from '~/core/scripts/utils';
import config from '../config';

const isSmallScreen = !is768Query();

function createTopThree(data, conf) {
  const mapItems = conf.filter(c => c.map);
  const topThree = mapItems.reduce((res, item) => {
    res[item.map] = [];
    return res;
  }, {});

  data.features.forEach(feat => {
    mapItems.forEach(confItem => {
      const value = feat.properties.partyItems.find(p => p.name === confItem.map).valueRel;

      if (value && isNumeric(value)) {
        topThree[confItem.map].push({
          value,
          name: feat.properties.Wahlgebiet_Name_Short || feat.properties.Wahlgebiet_Name
        });
      }
    });
  });

  mapItems.forEach(confItem => {
    topThree[confItem.map].sort(sortByKey('value'));
    topThree[confItem.map] = topThree[confItem.map].slice(0, 3);
  });

  return topThree;
}

class AnalysisChooser extends PureComponent {
  constructor(props) {
    super();

    this.analysisToggled = false;
    this.mapTopThree = createTopThree(props.data, config);
  }

  componentDidMount() {
    this.initSwiper();

    const parsedQuery = queryString.parse(window.location.search);
    const { maplocation = false } = parsedQuery;

    if (maplocation) {
      const geocoderPos = maplocation.split(',').map(d => +d);
      this.props.setGeocodePos(geocoderPos);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isAnalysisMode !== this.props.isAnalysisMode) {
      this.initSwiper();
    }
  }

  initSwiper() {
    if (!this.props.isAnalysisMode) {
      return false;
    }

    if (this.swiper) {
      this.swiper.destroy();
    }

    this.swiper = new Swiper(this.container, {
      slidesPerView: isSmallScreen ? 1 : 'auto',
      navigation: {
        nextEl: '.swipe__next',
        prevEl: '.swipe__prev'
      },
      on: {
        slideChange: () => {
          if (!isSmallScreen) {
            return false;
          }

          this.props.setAnalysis(config[this.swiper.activeIndex]);
        }
      }
    });
  }

  onToggleAnalysis(evt) {
    evt.stopPropagation();

    this.analysisToggled = true;

    if (isSmallScreen) {
      const nextAnalysis = this.props.isAnalysisMode ? {} : config[0];

      return this.props.toggleAnalysisMode(nextAnalysis);
    }

    this.props.toggleAnalysisMode();
  }

  onClose(evt) {
    evt.stopPropagation();
    this.props.setAnalysis(false);
  }

  onShowMore(evt) {
    evt.preventDefault();
    evt.stopPropagation();

    this.props.toggleAnalysisOverlay(true);
  }

  onSlideClick(slideData) {
    if (isSmallScreen) {
      return false;
    }

    this.analysisToggled = true;
    this.props.setAnalysis(slideData);
  }

  renderMapSlide(slideData, index) {
    const { selectedAnalysis, overallData } = this.props;
    const { valueRel } = overallData.find(d => d.name === slideData.map);
    const imageName = slideData.map === 'Grüne' ? 'Gruene' : slideData.map;

    return (
      <div
        key={`swiper__slide-${index}`}
        className={classnames('slide', 'swiper-slide', 'map', slideData.name, {
          'is-active': selectedAnalysis.name === slideData.name
        })}
        onClick={() => this.onSlideClick(slideData)}
      >
        {!isSmallScreen && (
          <div className="slide__close close btn" onClick={evt => this.onClose(evt)}>
            <Icon type="close" />
          </div>
        )}

        <div className="slide__wrapper">
          {!isSmallScreen && (
            <img
              className="slide__map"
              src={`images/map-screenshots/${ARTICLE_CONFIG.app.screenshotFolder.toLowerCase()}/${imageName}_2.png`}
            />
          )}

          <div className="slide__textwrapper">
            <div className="slide__title">
              <div className="slide__title-text">{slideData.label}</div>
              <div className="slide__title-value">{numberFormat(valueRel, 1)} %</div>
            </div>
            <div className="slide__description">
              {this.mapTopThree[slideData.map].map((d, i) => (
                <div className="slide__top" key={`top_${slideData.map}_${d.name}`}>
                  <div className="slide__top-rank">{i + 1}</div>
                  <div className="slide__top-name">{d.name}</div>
                  <div className="slide__top-value">{numberFormat(d.value, 1)} %</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {slideData.text && isSmallScreen && (
          <button className="slide__more" onClick={evt => this.onShowMore(evt)}>
            Mehr lesen
          </button>
        )}
      </div>
    );
  }

  renderSlide(slideData, index) {
    const { selectedAnalysis } = this.props;

    if (slideData.map) {
      return this.renderMapSlide(slideData, index);
    }

    return (
      <div
        key={`swiper__slide-${index}`}
        className={classnames('slide', 'swiper-slide', slideData.name, {
          'is-active': selectedAnalysis.name === slideData.name
        })}
        onClick={() => this.onSlideClick(slideData)}
      >
        {!isSmallScreen && (
          <div className="slide__close close btn" onClick={evt => this.onClose(evt)}>
            <Icon type="close" />
          </div>
        )}
        <div className="slide__title">
          {slideData.icon && <Icon type={slideData.icon} />}
          <div className="slide__title-text">{slideData.label}</div>
        </div>
        <div className="slide__description" dangerouslySetInnerHTML={{ __html: slideData.description }} />
        {slideData.text && isSmallScreen && (
          <button className="slide__more" onClick={evt => this.onShowMore(evt)}>
            Mehr lesen
          </button>
        )}
      </div>
    );
  }

  renderPagination() {
    const { selectedAnalysis } = this.props;

    return (
      <div className="analysischooser__pagination">
        <div className="swipe__prev swipe__btn">
          <Icon type="down" />
        </div>
        <div className="analysischooser__pagination-dotswrapper">
          <div className="analysischooser__pagination-dots">
            {config.map((d, i) => (
              <div
                key={`dot__${i}`}
                className={classnames('analysischooser__pagination-dot', { active: d.name === selectedAnalysis.name })}
              />
            ))}
          </div>
        </div>
        <div className="swipe__next swipe__btn">
          <Icon type="down" />
        </div>
      </div>
    );
  }

  renderSlides() {
    const { hasDataAnalysis, isAnalysisMode } = this.props;
    const showOverlay = hasDataAnalysis && !isAnalysisMode;

    return (
      <Fragment>
        {showOverlay && (
          <div className="analysischooser__overlay" onClick={evt => this.onToggleAnalysis(evt)}>
            <div className="analysischooser__overlay-text">Wahl-Analyse aktivieren</div>
          </div>
        )}
        {baseconfig.app.showAnalysisIntro && !isSmallScreen && (
          <div className="analysischooser__intro">
            <div
              className="analysischooser__intro-inner"
              dangerouslySetInnerHTML={{ __html: baseconfig.app.analysisIntroHtml }}
            />
          </div>
        )}
        {!isSmallScreen && (
          <div className="swipe__prev swipe__btn">
            <Icon type="down" />
          </div>
        )}
        <div ref={ref => (this.container = ref)} className="swiper-container">
          <div className="swiper-wrapper">{config.map((d, i) => this.renderSlide(d, i))}</div>
        </div>
        {!isSmallScreen && (
          <div className="swipe__next swipe__btn">
            <Icon type="down" />
          </div>
        )}
        {isSmallScreen && this.renderPagination()}
      </Fragment>
    );
  }

  renderResult() {
    return <OverallResult isCompact />;
  }

  render() {
    const { hasDataAnalysis, isAnalysisMode, selectedAnalysis } = this.props;
    const buttonLabel = isAnalysisMode ? 'Zurück zur Gewinner-Karte' : 'Hochburgen-Karte';
    const buttonIcon = isAnalysisMode ? 'arrow-left' : 'stats';
    const showMapIcon = !isAnalysisMode && isSmallScreen;
    const showActivateButton = isSmallScreen || hasDataAnalysis;
    const showCallToAction = !this.analysisToggled;
    const showSlides = !isSmallScreen || (isSmallScreen && isAnalysisMode);
    const chooserClasses = classnames('analysischooser', {
      'is-analysis': isAnalysisMode,
      'is-special-analysis': hasDataAnalysis,
      [`slidename-${selectedAnalysis.name}`]: selectedAnalysis.name
    });
    const activateBtnClasses = classnames('activate', 'btn', 'circle', { 'is-analysis': isAnalysisMode });

    return (
      <div className={chooserClasses}>
        {showCallToAction && (
          <div className="analysischooser__cta">
            <div>{isSmallScreen ? baseconfig.app.ctaTextMobile : baseconfig.app.ctaText}</div>
            <Icon type="arrow-curved" />
          </div>
        )}
        {showActivateButton && (
          <button className={activateBtnClasses} onClick={evt => this.onToggleAnalysis(evt)}>
            {!showMapIcon && <Icon type={buttonIcon} key={buttonIcon} />}
            {showMapIcon && (
              <img
                className="btn-map"
                src={`images/map-screenshots/${ARTICLE_CONFIG.app.screenshotFolder.toLowerCase()}/AfD_2.png`}
              />
            )}
            <div className="activate__label">{buttonLabel}</div>
          </button>
        )}
        {showSlides ? this.renderSlides() : this.renderResult()}
      </div>
    );
  }
}

export default connect(
  state => ({
    selectedAnalysis: state.selectedAnalysis,
    hasDataAnalysis: state.hasDataAnalysis,
    isAnalysisMode: state.isAnalysisMode
  }),
  actions
)(AnalysisChooser);
